import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import * as api from '../../api/api';

const statusOptions = ['Active', 'Maintenance', 'Sunsetting', 'Inactive'];

function ApplicationsTab() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', status: 'Active' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newApplication, setNewApplication] = useState({ name: '', status: 'Active' });

    const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.getApplications();
      console.log('ApplicationsTab: Raw API response:', response);
      
      // Handle different response formats
      let apps = [];
      if (Array.isArray(response)) {
        apps = response;
      } else if (response && Array.isArray(response.applications)) {
        apps = response.applications;
      } else {
        console.warn('ApplicationsTab: Unexpected response format:', response);
        apps = [];
      }
      
      console.log('ApplicationsTab: Processed applications:', apps);
      setApplications(apps);
      setError('');
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleEdit = (app) => {
    setEditingId(app.id);
    setEditForm({ name: app.name, status: app.status });
  };

    const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      setError('Application name is required');
      return;
    }

    try {
      const response = await api.updateApplication(editingId, editForm);
      console.log('Application updated:', response);
      
      setEditingId(null);
      setEditForm({ name: '', status: 'Active' });
      await fetchApplications();
      setError('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('applicationUpdated', {
        detail: { applicationId: editingId, applicationData: editForm }
      }));
      
    } catch (err) {
      console.error('Error updating application:', err);
      setError('Failed to update application');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', status: 'Active' });
  };

    const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        const response = await api.deleteApplication(id);
        console.log('Application deleted:', response);
        
        await fetchApplications();
        setError('');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('applicationDeleted', {
          detail: { applicationId: id }
        }));
        
      } catch (err) {
        console.error('Error deleting application:', err);
        setError('Failed to delete application');
      }
    }
  };

  const handleAddNew = async () => {
    if (!newApplication.name.trim()) {
      setError('Application name is required');
      return;
    }

            try {
      console.log('Creating application from AdminPage:', newApplication);
      const response = await api.createApplication(newApplication);
      console.log('Application creation response from AdminPage:', response);
      
      setDialogOpen(false);
      setNewApplication({ name: '', status: 'Active' });
      await fetchApplications();
      setError('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('applicationCreated', {
        detail: { applicationData: response }
      }));
      
    } catch (err) {
      console.error('Error creating application:', err);
      setError('Failed to create application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Maintenance': return 'warning';
      case 'Sunsetting': return 'error';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const filteredApplications = applications
    .filter(app => 
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (statusFilter === 'All' || app.status === statusFilter)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Applications Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          Add Application
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Search Applications"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {statusOptions.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredApplications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {searchTerm || statusFilter !== 'All' ? 'No applications match your filters' : 'No applications found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredApplications.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    {editingId === app.id ? (
                      <TextField
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      app.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === app.id ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        >
                          {statusOptions.map(status => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={app.status}
                        color={getStatusColor(app.status)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingId === app.id ? (
                      <Box>
                        <IconButton
                          onClick={handleSaveEdit}
                          color="primary"
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleCancelEdit}
                          color="secondary"
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <IconButton
                          onClick={() => handleEdit(app)}
                          style={{ color: "#90caf9" }}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(app.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add New Application Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Application</DialogTitle>
        <DialogContent>
          <TextField
            label="Application Name"
            value={newApplication.name}
            onChange={(e) => setNewApplication({ ...newApplication, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={newApplication.status}
              label="Status"
              onChange={(e) => setNewApplication({ ...newApplication, status: e.target.value })}
            >
              {statusOptions.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNew} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApplicationsTab;